import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Input, List, Button, Space, Tag, Typography, Empty, Alert, Switch } from 'antd';
import { dviApi } from '../../services/api/dviApi';
import { DVIInspectionItem, JobTemplateOption } from '../../types/dvi';

const { Text } = Typography;

export interface JobTemplatePickerModalProps {
  open: boolean;
  inspectionId: number;
  item: DVIInspectionItem;
  estimateId: number;
  onCancel: () => void;
  /** Applied successfully — the parent reloads the estimate's Service Items. */
  onApplied: (result: { duplicate: boolean; warnings: string[] }) => void;
  /**
   * Quote this finding without a template, i.e. the previous behaviour. Shown
   * as a way out, not as the main path — see the note in the component.
   */
  onAddWithoutTemplate: () => Promise<void>;
}

const money = (n: number | null | undefined): string =>
  `$${(n ?? 0).toFixed(2)}`;

/**
 * WP-09 — pick the job for an inspection finding.
 *
 * Owner's request: "When doing an inspection and clicking add to estimate i
 * want to have a selectable template for that job like front brakes, European
 * front brakes ... and only show the selectable templates."
 *
 * Two things it deliberately does NOT do:
 *
 *  - It does not price anything. Every figure shown comes from the server,
 *    which resolves the template against the shop's current labor rates and
 *    markup presets. The apply call sends ids only. A picker that computed its
 *    own totals would be a second pricing implementation to keep in step.
 *  - It does not become a dead end. When the owner has flagged no templates —
 *    a brand new shop, or the very first day after this ships — "Add without a
 *    template" still does exactly what the button did before. A mechanic
 *    standing at a car must never be blocked by unfinished configuration.
 */
export const JobTemplatePickerModal: React.FC<JobTemplatePickerModalProps> = ({
  open, inspectionId, item, estimateId, onCancel, onApplied, onAddWithoutTemplate,
}) => {
  const [templates, setTemplates] = useState<JobTemplateOption[]>([]);
  const [category, setCategory] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [applyingId, setApplyingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (all: boolean, q: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await dviApi.listJobTemplates(inspectionId, item.id, {
        all, search: q || undefined,
      });
      setTemplates(res.templates);
      setCategory(res.category);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Could not load job templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [inspectionId, item.id]);

  // One effect, not two. An "open" effect plus a debounced "filters changed"
  // effect both fire on mount, which fetches twice and — because the second
  // lands 300ms later and clears the error banner — can silently erase a
  // failure the technician needs to see. The first load after opening is
  // immediate; every later one is debounced so typing does not spam the server.
  const firstLoadDone = useRef(false);
  useEffect(() => {
    if (!open) {
      firstLoadDone.current = false;
      return;
    }
    if (!firstLoadDone.current) {
      firstLoadDone.current = true;
      load(showAll, search);
      return;
    }
    const h = setTimeout(() => load(showAll, search), 300);
    return () => clearTimeout(h);
  }, [open, showAll, search, load]);

  const handleApply = async (t: JobTemplateOption) => {
    setApplyingId(t.id);
    setError(null);
    try {
      const res = await dviApi.applyJobTemplate(inspectionId, item.id, {
        templateId: t.id, estimateId,
      });
      onApplied({ duplicate: res.duplicate, warnings: res.warnings ?? [] });
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Could not add this job to the estimate');
    } finally {
      setApplyingId(null);
    }
  };

  const [addingBare, setAddingBare] = useState(false);
  const handleBare = async () => {
    setAddingBare(true);
    setError(null);
    try {
      await onAddWithoutTemplate();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Could not add the line');
    } finally {
      setAddingBare(false);
    }
  };

  const filterLabel = category
    ? `Showing ${category} jobs`
    : 'Showing every job in the picker';

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={`Add to estimate — ${item.point_name}`}
      footer={
        <Button onClick={handleBare} loading={addingBare} data-testid="jtp-no-template">
          Add without a template
        </Button>
      }
      centered
      width={640}
      // The primary use is a phone at the car; the modal must not exceed it.
      styles={{ body: { maxHeight: '60vh', overflowY: 'auto' } }}
      destroyOnHidden
    >
      {error && (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
      )}

      <Input.Search
        placeholder="Search all job templates…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        aria-label="Search job templates"
        style={{ marginBottom: 8 }}
      />

      {category && !search && (
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 8, marginBottom: 12, flexWrap: 'wrap',
          }}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            {showAll ? 'Showing every job in the picker' : filterLabel}
          </Text>
          <Space size={6}>
            <Text type="secondary" style={{ fontSize: 12 }}>Show all</Text>
            <Switch
              size="small"
              checked={showAll}
              onChange={setShowAll}
              aria-label="Show all job templates"
              data-testid="jtp-show-all"
            />
          </Space>
        </div>
      )}

      <List
        loading={loading}
        dataSource={templates}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  {search || showAll
                    ? 'No job templates match.'
                    : `No ${category ?? ''} jobs in the quick picker.`}
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    An owner adds jobs to the picker from Templates.
                  </Text>
                </span>
              }
            />
          ),
        }}
        renderItem={(t) => (
          <List.Item
            data-testid={`jtp-option-${t.id}`}
            actions={[
              <Button
                key="apply"
                type="primary"
                size="small"
                loading={applyingId === t.id}
                disabled={applyingId != null && applyingId !== t.id}
                onClick={() => handleApply(t)}
              >
                Add
              </Button>,
            ]}
          >
            <List.Item.Meta
              title={
                <Space size={6} wrap>
                  <span>{t.name}</span>
                  {t.category && (
                    <Tag color={t.category_match ? 'blue' : 'default'}>{t.category}</Tag>
                  )}
                  {t.labor_rate_missing && (
                    <Tag color="warning">rate removed</Tag>
                  )}
                </Space>
              }
              description={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t.labor_hours ?? 0}h
                  {t.hourly_rate != null ? ` @ ${money(t.hourly_rate)}/hr` : ''}
                  {` · ${t.part_count} part${t.part_count === 1 ? '' : 's'}`}
                  {` · ${money(t.estimated_total)}`}
                </Text>
              }
            />
          </List.Item>
        )}
      />
    </Modal>
  );
};

export default JobTemplatePickerModal;
